import { Bar, BarChart, CartesianGrid, Cell, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import type { RevenueTotal } from "../api";
import { money, monthLabel } from "./format";

/**
 * Seven months of one currency, bot and dialer stacked.
 *
 * Stacked rather than grouped because the total is the question and the
 * split is the detail: the height is the month's revenue, the two colours
 * say where it came from. One chart per currency, never one axis for two.
 *
 * The current month is drawn faded with "to date" on its label -- a month
 * a third over beside complete ones always reads as a collapse.
 */
export default function RevenueChart({
  currency,
  months,
  totals,
  selected,
}: {
  currency: string;
  months: string[];
  totals: RevenueTotal[];
  selected: string;
}) {
  const current = months[0];
  const rows = [...months].reverse().map((m) => {
    const ts = totals.filter((t) => t.month === m);
    const sum = (k: "bot_amount" | "dialer_amount") => ts.reduce((a, t) => a + Number(t[k]), 0);
    return {
      month: m,
      label: m === current ? `${monthLabel(m, true)} (to date)` : monthLabel(m, true),
      bot: sum("bot_amount"),
      dialer: sum("dialer_amount"),
    };
  });
  if (rows.every((r) => r.bot === 0 && r.dialer === 0)) return null;
  const hasDialer = rows.some((r) => r.dialer > 0);

  return (
    <figure className="rev-chart" aria-label={`Revenue by month, ${currency}`}>
      <figcaption>
        <span className="plot-title">Revenue by month ({currency})</span>
        <span className="dim">Customers and unmapped tenants</span>
      </figcaption>
      <ResponsiveContainer width="100%" height={200}>
        <BarChart data={rows} margin={{ top: 8, right: 8, bottom: 0, left: 8 }}>
          <CartesianGrid vertical={false} stroke="var(--line)" />
          <XAxis dataKey="label" tickLine={false} axisLine={{ stroke: "var(--line)" }} tick={{ fill: "var(--muted)", fontSize: 12 }} />
          <YAxis
            tickFormatter={(v: number) => money(v, currency)}
            tickLine={false}
            axisLine={false}
            width={70}
            tick={{ fill: "var(--muted)", fontSize: 12 }}
          />
          <Tooltip
            cursor={{ fill: "var(--line)", opacity: 0.4 }}
            formatter={(v, name) => [money(Number(v), currency, true), String(name)]}
            contentStyle={{ background: "var(--card)", border: "1px solid var(--line)", color: "var(--fg)" }}
          />
          {hasDialer && <Legend wrapperStyle={{ fontSize: 12, color: "var(--muted)" }} />}
          <Bar
            dataKey="bot"
            name="AI voice bot"
            stackId="r"
            fill="var(--viz-series-1)"
            stroke="var(--card)"
            strokeWidth={1}
            radius={hasDialer ? 0 : [4, 4, 0, 0]}
          >
            {rows.map((r) => (
              <Cell key={r.month} fillOpacity={emphasis(r.month, selected, current)} />
            ))}
          </Bar>
          {hasDialer && (
            <Bar
              dataKey="dialer"
              name="Dialer"
              stackId="r"
              fill="var(--viz-series-2)"
              stroke="var(--card)"
              strokeWidth={1}
              radius={[4, 4, 0, 0]}
            >
              {rows.map((r) => (
                <Cell key={r.month} fillOpacity={emphasis(r.month, selected, current)} />
              ))}
            </Bar>
          )}
        </BarChart>
      </ResponsiveContainer>
    </figure>
  );
}

/** Full strength for the month picked, faded for the month in progress. */
function emphasis(month: string, selected: string, current: string): number {
  return month === selected ? 1 : month === current ? 0.35 : 0.6;
}
